import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Mail, Inbox, Trash2, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const AdminInbox = () => {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all"); // all | unread | read | replied
  const load = async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? `${API}/admin/contact-messages` : `${API}/admin/contact-messages?status=${filter}`;
      const res = await axios.get(url);
      setMessages(res.data.messages || []);
      setUnread(res.data.unread || 0);
    } catch (e) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (m, status) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${m.message_id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${m.message_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggle = (id) => {
    setExpanded((s) => ({ ...s, [id]: !s[id] }));
    const msg = messages.find((m) => m.message_id === id);
    if (msg && msg.status === "unread") setStatus(msg, "read");
  };

  const filterLabels = [
    { key: "all", label: "All" },
    { key: "unread", label: `Unread (${unread})` },
    { key: "read", label: "Read" },
    { key: "replied", label: "Replied" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-inbox">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#002FA7]" /> Contact Inbox
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Messages submitted via the public /contact form.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-3 py-2 border border-zinc-800 rounded"
          data-testid="inbox-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-1 bg-zinc-800 p-0.5 rounded w-fit">
        {filterLabels.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              filter === f.key ? "bg-zinc-900 text-white" : "text-zinc-400 hover:text-white"
            }`}
            data-testid={`inbox-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center h-32 items-center">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 p-12 text-center text-sm text-zinc-500" data-testid="inbox-empty">
          <Mail className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
          No messages here yet.
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 divide-y divide-zinc-800">
          {messages.map((m) => {
            const open = !!expanded[m.message_id];
            return (
              <div key={m.message_id} className="p-4" data-testid={`inbox-msg-${m.message_id}`}>
                <button
                  onClick={() => toggle(m.message_id)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  {open ? <ChevronDown className="w-4 h-4 mt-1 text-zinc-400" /> : <ChevronRight className="w-4 h-4 mt-1 text-zinc-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{m.subject}</span>
                      {m.status === "unread" && (
                        <span className="text-[10px] uppercase tracking-wide bg-[#002FA7] text-white px-1.5 py-0.5 rounded">new</span>
                      )}
                      {m.status === "replied" && (
                        <span className="text-[10px] uppercase tracking-wide bg-emerald-700 text-white px-1.5 py-0.5 rounded">replied</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 truncate">
                      {m.name} · <a href={`mailto:${m.email}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{m.email}</a> · {new Date(m.created_at).toLocaleString()}
                    </p>
                  </div>
                </button>
                {open && (
                  <div className="mt-3 ml-7 space-y-3">
                    <pre className="text-sm text-zinc-200 bg-black/40 border border-zinc-800 rounded p-3 whitespace-pre-wrap break-words" data-testid={`inbox-body-${m.message_id}`}>
                      {m.message}
                    </pre>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                        onClick={() => setStatus(m, "replied")}
                        className="text-xs px-3 py-1.5 bg-[#002FA7] hover:bg-[#00227A] text-white rounded"
                        data-testid={`inbox-reply-${m.message_id}`}
                      >
                        Reply via email
                      </a>
                      <button
                        onClick={() => setStatus(m, m.status === "replied" ? "read" : "replied")}
                        className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:text-white rounded"
                      >
                        {m.status === "replied" ? "Mark unreplied" : "Mark replied"}
                      </button>
                      <button
                        onClick={() => remove(m)}
                        className="text-xs px-3 py-1.5 border border-red-800 text-red-400 hover:text-red-300 rounded inline-flex items-center gap-1.5"
                        data-testid={`inbox-delete-${m.message_id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
